import { useState } from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import EvStationIcon from '@mui/icons-material/EvStation'
import EnergySavingsLeafIcon from '@mui/icons-material/EnergySavingsLeaf'
import SolarPowerIcon from '@mui/icons-material/SolarPower'
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Chip from '@mui/material/Chip'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'

import { SessionSkeleton as ChargerSkeleton } from './Sessions'

// Finished sessions on this charger are reported to a Charge2Save fleet,
// e.g. the company of the employee whose home charger this is
const FleetSettings = ({ charger, setFleet }) => {
  const [apiKey, setApiKey] = useState('')
  const [cardNumber, setCardNumber] = useState(charger.fleet?.cardNumber ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState()

  const save = async (fleet) => {
    setSaving(true)
    setError(undefined)
    const reason = await setFleet(charger, fleet)
    setSaving(false)
    if (reason) setError(reason)
    else setApiKey('')
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Charge2Save Fleet
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {charger.fleet
          ? `Finished sessions are reported to the fleet with key ${charger.fleet.keyHint}.`
          : 'Report finished sessions to your company\'s fleet, to earn credits for charging at home.'}
      </Typography>
      <TextField
        label={charger.fleet ? 'New fleet API key' : 'Fleet API key'}
        placeholder="c2sf_…"
        type="password"
        autoComplete="off"
        size="small"
        fullWidth
        margin="dense"
        value={apiKey}
        onChange={(e) => setApiKey(e.target.value)}
      />
      <TextField
        label="Fleet card number (optional)"
        helperText="Without it, the RFID card used to start the session is credited."
        size="small"
        fullWidth
        margin="dense"
        value={cardNumber}
        onChange={(e) => setCardNumber(e.target.value)}
      />
      {error && (
        <Typography variant="body2" color="error" role="alert">
          {error}
        </Typography>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          variant="contained"
          size="small"
          disabled={saving || (!apiKey && !charger.fleet)}
          onClick={() => save({ apiKey: apiKey || undefined, cardNumber })}
        >
          Save
        </Button>
        {charger.fleet && (
          <Button size="small" color="error" disabled={saving} onClick={() => save({ apiKey: '' })}>
            Stop reporting
          </Button>
        )}
      </Stack>
    </Box>
  )
}

const ChargerCard = ({ charger, setChargingMode, setFleet }) => {
  const [expanded, setExpanded] = useState(false)
  const [mode, setMode] = useState(charger.chargingMode)
  const [chargingModeConfirmed, setChargingModeConfirmed] = useState(true)

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const handleModeChange = async (_e, newMode) => {
    if (newMode === null || newMode === mode) return;

    const oldMode = mode;
    setMode(newMode);
    setChargingModeConfirmed(false);

    // TODO: would be good to put a timeout on the promise
    //       such that users don't need to wait e.g. 1 minute
    //       for the HTTP request to timeout

    const success = await setChargingMode(charger, newMode);
    if (success) {
      setChargingModeConfirmed(true);
    } else {
      // revert the UI update
      // TODO: useOptimistic could help as we would not need to manually
      //       revert the UI update
      setMode(oldMode);
      setChargingModeConfirmed(true);
    }
  }

  const getModeIcon = (currentMode) => {
    switch (currentMode) {
      case 'TRADITIONAL':
        return <ElectricalServicesIcon sx={{ mr: 1, color: mode === 'TRADITIONAL' ? 'primary.main' : 'inherit' }} />
      case 'ECO':
        return <EnergySavingsLeafIcon sx={{ mr: 1, color: mode === 'ECO' ? 'primary.main' : 'inherit' }} />
      case 'GREEN':
        return <SolarPowerIcon sx={{ mr: 1, color: mode === 'GREEN' ? 'primary.main' : 'inherit' }} />
      default:
        return <ElectricalServicesIcon sx={{ mr: 1 }} />
    }
  }

  return (
    <Card sx={{ minWidth: 275, mb: 2 }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <EvStationIcon color="primary" sx={{ fontSize: 40 }} />
          <div>
            <Typography variant="h6" component="div">
              {charger.description}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {charger.address}
            </Typography>
          </div>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <Chip 
            label={charger.isOnline ? "Online" : "Offline"}
            color={charger.isOnline ? "success" : "error"}
            size="small"
          />
          
          <Stack direction="row" alignItems="center">
            <ToggleButtonGroup
              exclusive
              value={mode}
              onChange={handleModeChange}
              size="small"
              disabled={!chargingModeConfirmed}
            >
              <ToggleButton value="TRADITIONAL">
                {getModeIcon('TRADITIONAL')}
                Traditional
              </ToggleButton>
              <ToggleButton value="ECO">
                {getModeIcon('ECO')}
                Eco
              </ToggleButton>
              <ToggleButton value="GREEN">
                {getModeIcon('GREEN')}
                Green
              </ToggleButton>
            </ToggleButtonGroup>
            {!chargingModeConfirmed && (
              <Typography variant="body2" sx={{ ml: 1 }}>
                Mode change requested...
              </Typography>
            )}
          </Stack>
        </Stack>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer'
          }}
          onClick={handleExpandClick}
        >
          <IconButton
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: '0.3s'
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Chargepoint ID: {charger.chargepointId}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Number of Connectors: {charger.connectorCount}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Price per kWh: €{(charger.pricePerKWh / 100).toFixed(2)}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Max Amperage: {charger.maxAmperage}A
            </Typography>
            {charger.backendUrl && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Backend URL: {charger.backendUrl}
              </Typography>
            )}
            {setFleet && <FleetSettings charger={charger} setFleet={setFleet} />}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  )
}

export const Chargers = ({ chargers, setChargingMode, setFleet }) => {
  const [expanded, setExpanded] = useState(true)

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const content = !chargers || chargers.length === 0 ? (
    <Typography variant="body1" color="text.secondary">
      No chargers found
    </Typography>
  ) : (
    chargers.map((charger) => (
      <ChargerCard key={charger.chargepointId} charger={charger} setChargingMode={setChargingMode} setFleet={setFleet} />
    ))
  )

  return (
    <Box sx={{ mt: 2, width: '100%', maxWidth: '400px' }}>
      <Box 
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer'
        }}
        onClick={handleExpandClick}
      >
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          My Chargers
        </Typography>
        <IconButton
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: '0.3s'
          }}
        >
          <ExpandMoreIcon />
        </IconButton>
      </Box>
      <Collapse in={expanded} timeout="auto" sx={{ mt: 1 }}>
        {
          !chargers ? (
            <ChargerSkeleton />
          ) :
            content
        }
      </Collapse>
    </Box>
  )
}
